import React, { useEffect, useMemo, useState } from 'react';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { RiskProfilePanel } from '@/components/participants/risk/RiskProfilePanel';
import { ReadinessSummaryPanel } from '@/components/participants/readiness/ReadinessSummaryPanel';
import { OnboardingChecklistPanel } from '@/components/participants/onboarding/OnboardingChecklistPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ChevronRight, AlertTriangle, X, Plus, ArrowLeft, Send, Trash2 } from 'lucide-react';

// ─── Section catalogue ──────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'demographics',  label: 'Demographics',   required: true,  data_kind: 'object' },
  { key: 'ndis_funding',  label: 'NDIS & Funding', required: true,  data_kind: 'object' },
  { key: 'services',      label: 'Services',       required: true,  data_kind: 'array'  },
  { key: 'contacts',      label: 'Emergency Contacts', required: true, data_kind: 'array' },
  { key: 'consents',      label: 'Consents',       required: true,  data_kind: 'object' },
  { key: 'goals',         label: 'Goals & Preferences', required: false, data_kind: 'object' },
  { key: 'accommodation', label: 'Accommodation',  required: false, data_kind: 'object' },
  { key: 'clinical',      label: 'Clinical Baseline', required: false, data_kind: 'object' },
  { key: 'documents',     label: 'Documents',      required: false, data_kind: 'array'  },
];

const RELATIONSHIPS = ['PARENT','GUARDIAN','SIBLING','PARTNER','FRIEND','CHILD','CARER','SUPPORT_COORDINATOR','OTHER'];
const PLAN_MGMT = ['NDIA_MANAGED','PLAN_MANAGED','SELF_MANAGED','SELF_MANAGED_AGENCY'];
const FUNDING_SRC = ['NDIS','HCP','PRIVATE','OTHER'];
const ALLERGY_TYPES = ['DRUG','FOOD','ENVIRONMENTAL','LATEX','INSECT','OTHER'];
const ALLERGY_SEVS = ['MILD','MODERATE','SEVERE','ANAPHYLAXIS'];
const COND_CATEGORIES = ['CHRONIC','ACUTE','MENTAL_HEALTH','SENSORY','MOBILITY','OTHER'];
const COND_STATUSES = ['ACTIVE','IN_REMISSION','RESOLVED','SUSPECTED'];
const COND_SEVS = ['LOW','MODERATE','HIGH'];

// ─── Wizard root ────────────────────────────────────────────────────────────
export default function ParticipantIntakeWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [activeKey, setActiveKey] = useState('demographics');
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);

  const load = async () => {
    try {
      const r = await api.get(`/intake/participants/${id}`);
      setIntake(r.data.data);
    } catch (e) {
      toast.error('Could not load intake.');
      navigate('/intake/participants');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    api.get('/services/catalog?mode=selection').then(r => setServiceCatalog(r.data.data || [])).catch(() => {});
  }, []);

  const readiness = intake?.readiness || { required_complete: false, missing: [], optional_warnings: [], completed_sections: [] };
  const sections = intake?.sections || {};
  const status = intake?.status;
  const terminal = status === 'ACTIVATED' || status === 'INTAKE_DECLINED';

  const saveSection = async (key, data, markCompleted) => {
    setSaving(true);
    try {
      const r = await api.patch(`/intake/participants/${id}/sections/${key}`, {
        data,
        mark_completed: !!markCompleted,
      });
      setIntake({ ...r.data.data, readiness: r.data.readiness });
      if (markCompleted) toast.success('Section saved');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitReview = async () => {
    try {
      const r = await api.post(`/intake/participants/${id}/submit-review`);
      setIntake({ ...intake, status: r.data.data.status });
      toast.success('Submitted for review');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Submit failed');
    }
  };

  const activate = async () => {
    setActivating(true);
    try {
      const r = await api.post(`/intake/participants/${id}/activate`);
      const body = r.data.data;
      const warnCount = (body.warnings || []).length;
      if (warnCount > 0) {
        toast.warning(`Activated with ${warnCount} warning(s). Review the participant page.`);
      } else {
        toast.success('Participant activated!');
      }
      navigate(`/participants/${body.participant_id}`);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Activation failed';
      const code = e?.response?.data?.error?.code;
      toast.error(`${msg}${code ? ` (${code})` : ''}`);
    } finally {
      setActivating(false);
    }
  };

  const declineIntake = async () => {
    const reason = window.prompt('Reason for declining (min 10 chars):');
    if (!reason || reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    try {
      const r = await api.post(`/intake/participants/${id}/decline`, { reason });
      setIntake({ ...intake, status: r.data.data.status });
      toast.success('Intake declined');
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Decline failed');
    }
  };

  if (!intake) return <div className="p-6">Loading…</div>;

  const activeSec = sections[activeKey] || { data: SECTIONS.find(s => s.key === activeKey)?.data_kind === 'array' ? [] : {}, completed_at: null };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="intake-wizard">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/intake/participants')} data-testid="intake-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold flex-1">Participant Intake</h1>
        <Badge variant="outline" data-testid="intake-status-badge">{status}</Badge>
      </div>

      <ReadinessBanner readiness={readiness} status={status} />

      {/* Phase 2E PR-2 — Participant Readiness Engine preview (informational only).
          Renders an empty-state placeholder before activation, or the live engine
          verdict once the intake has been promoted to a participant. Feature-flag
          gated by `participant_readiness_engine_enabled` (default OFF). */}
      <div className="mt-3">
        <ReadinessSummaryPanel mode="banner" intake={intake} />
      </div>

      {/* Phase 2F (Feb 2026) — Onboarding Checklist preview (informational only).
          Renders nothing pre-activation or when its feature flag is OFF. */}
      <div className="mt-3">
        <OnboardingChecklistPanel mode="banner" intake={intake} />
      </div>

      <div className="grid grid-cols-12 gap-6 mt-4">
        {/* Stepper */}
        <div className="col-span-3" data-testid="intake-stepper">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Sections</CardTitle></CardHeader>
            <CardContent className="space-y-1 pt-0">
              {SECTIONS.map(s => {
                const sec = sections[s.key] || {};
                const complete = !!sec.completed_at;
                const missing = s.required && readiness.missing?.includes(s.key);
                const isActive = activeKey === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveKey(s.key)}
                    data-testid={`intake-step-${s.key}`}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${isActive ? 'bg-primary/10 font-semibold' : 'hover:bg-muted/50'}`}
                  >
                    {complete
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      : missing
                        ? <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                        : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <span className="flex-1">{s.label}</span>
                    {s.required && <span className="text-[10px] text-muted-foreground">REQ</span>}
                    {isActive && <ChevronRight className="h-3 w-3" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Section editor */}
        <div className="col-span-9" data-testid={`intake-section-${activeKey}`}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{SECTIONS.find(s => s.key === activeKey)?.label}</span>
                {activeSec.completed_at && <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SectionEditor
                sectionKey={activeKey}
                data={activeSec.data}
                disabled={terminal}
                serviceCatalog={serviceCatalog}
                onSave={(data, completed) => saveSection(activeKey, data, completed)}
                saving={saving}
              />
            </CardContent>
          </Card>

          {/* Activation footer */}
          <div className="mt-4 flex items-center justify-end gap-2">
            {status === 'INTAKE_DRAFT' && (
              <Button variant="outline" onClick={submitReview} disabled={!readiness.required_complete} data-testid="intake-submit-review-btn">
                <Send className="h-4 w-4 mr-1" /> Submit for review
              </Button>
            )}
            {!terminal && (
              <Button variant="ghost" onClick={declineIntake} className="text-rose-600" data-testid="intake-decline-btn">
                <X className="h-4 w-4 mr-1" /> Decline
              </Button>
            )}
            {!terminal && (
              <Button
                onClick={activate}
                disabled={!readiness.required_complete || activating}
                title={readiness.required_complete ? '' : `Complete required sections first: ${(readiness.missing || []).join(', ')}`}
                data-testid="intake-activate-btn"
              >
                {activating ? 'Activating…' : 'Activate Participant'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Readiness banner ───────────────────────────────────────────────────────
function ReadinessBanner({ readiness, status }) {
  if (status === 'ACTIVATED') {
    return (
      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-900" data-testid="intake-readiness-banner">
        <CheckCircle2 className="h-4 w-4 inline mr-1" /> Activated — participant has been created.
      </div>
    );
  }
  if (status === 'INTAKE_DECLINED') {
    return (
      <div className="rounded-md bg-rose-50 border border-rose-200 px-4 py-2 text-sm text-rose-900" data-testid="intake-readiness-banner">
        <X className="h-4 w-4 inline mr-1" /> This intake was declined.
      </div>
    );
  }
  if (readiness.required_complete) {
    return (
      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-900" data-testid="intake-readiness-banner">
        <CheckCircle2 className="h-4 w-4 inline mr-1" /> All required sections complete. Ready to activate.
      </div>
    );
  }
  return (
    <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-900" data-testid="intake-readiness-banner">
      <AlertTriangle className="h-4 w-4 inline mr-1" />
      Required sections remaining: {(readiness.missing || []).join(', ') || '—'}
    </div>
  );
}

// ─── Section editor router ──────────────────────────────────────────────────
function SectionEditor({ sectionKey, data, onSave, saving, disabled, serviceCatalog }) {
  switch (sectionKey) {
    case 'demographics':  return <DemographicsForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'ndis_funding':  return <NdisFundingForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'services':      return <ServicesForm data={Array.isArray(data) ? data : []} onSave={onSave} saving={saving} disabled={disabled} catalog={serviceCatalog} />;
    case 'contacts':      return <ContactsForm data={Array.isArray(data) ? data : []} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'consents':      return <ConsentsForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'goals':         return <GoalsForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'accommodation': return <AccommodationForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'clinical':      return <ClinicalForm data={data || {}} onSave={onSave} saving={saving} disabled={disabled} />;
    case 'documents':     return <DocumentsForm data={Array.isArray(data) ? data : []} onSave={onSave} saving={saving} disabled={disabled} />;
    default: return <div className="text-sm text-muted-foreground">Unknown section.</div>;
  }
}

// ─── Form helpers ───────────────────────────────────────────────────────────
function FormShell({ children, onSave, saving, disabled, dirty, setDirty, completeBlocked, completeBlockedReason }) {
  return (
    <form className="space-y-4" onChange={() => setDirty(true)} data-testid="intake-section-form">
      <div className="space-y-3">{children}</div>
      <div className="flex flex-col gap-1 pt-2 border-t">
        <div className="flex gap-2">
          <Button type="button" variant="outline" disabled={saving || disabled} onClick={() => onSave(false)} data-testid="intake-section-save-draft">Save draft</Button>
          <Button
            type="button"
            disabled={saving || disabled || !!completeBlocked}
            onClick={() => onSave(true)}
            data-testid="intake-section-save-complete"
            title={completeBlocked ? (completeBlockedReason || 'Section is not ready to be marked complete.') : undefined}
          >
            Save &amp; mark complete
          </Button>
        </div>
        {completeBlocked && completeBlockedReason && (
          <p className="text-xs text-amber-700" data-testid="intake-section-complete-blocked-reason">
            {completeBlockedReason}
          </p>
        )}
      </div>
    </form>
  );
}

// ─── Demographics ───────────────────────────────────────────────────────────
const AU_STATE_OPTIONS = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' },
];

function DemographicsForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ first_name: '', last_name: '', date_of_birth: '', ndis_number: '', email: '', mobile: '', street_address: '', suburb: '', state: '', postcode: '', preferred_name: '', gender: '', primary_language: '', cultural_background: '', interpreter_required: false, ...data });
  const [dirty, setDirty] = useState(false);
  const isBlank = (v) => !v || !String(v).trim();
  const missingLabels = [
    ['NDIS number', d.ndis_number],
    ['Email', d.email],
    ['Mobile', d.mobile],
    ['Street Address', d.street_address],
    ['Suburb', d.suburb],
    ['State', d.state],
    ['Postcode', d.postcode],
  ].filter(([, v]) => isBlank(v)).map(([l]) => l);
  const completeBlocked = missingLabels.length > 0;
  const handleSave = (complete) => onSave({ ...d }, complete);
  return (
    <FormShell onSave={handleSave} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}
      completeBlocked={completeBlocked}
      completeBlockedReason={`Required to mark Demographics complete: ${missingLabels.join(', ')}.`}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name *"><Input value={d.first_name} onChange={e => setD({ ...d, first_name: e.target.value })} disabled={disabled} data-testid="intake-demo-firstname" /></Field>
        <Field label="Last name *"><Input value={d.last_name} onChange={e => setD({ ...d, last_name: e.target.value })} disabled={disabled} data-testid="intake-demo-lastname" /></Field>
        <Field label="Preferred name"><Input value={d.preferred_name} onChange={e => setD({ ...d, preferred_name: e.target.value })} disabled={disabled} /></Field>
        <Field label="Date of birth"><Input type="date" value={d.date_of_birth} onChange={e => setD({ ...d, date_of_birth: e.target.value })} disabled={disabled} /></Field>
        <Field label="NDIS number *"><Input value={d.ndis_number} onChange={e => setD({ ...d, ndis_number: e.target.value })} disabled={disabled} data-testid="intake-demo-ndis" /></Field>
        <Field label="Gender">
          <Select value={d.gender || ''} onValueChange={(v) => setD({ ...d, gender: v })} disabled={disabled}>
            <SelectTrigger data-testid="intake-demo-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Male" data-testid="intake-demo-gender-male">Male</SelectItem>
              <SelectItem value="Female" data-testid="intake-demo-gender-female">Female</SelectItem>
              <SelectItem value="Other" data-testid="intake-demo-gender-other">Other</SelectItem>
              <SelectItem value="Prefer not to say" data-testid="intake-demo-gender-prefer-not">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Email *"><Input type="email" value={d.email} onChange={e => setD({ ...d, email: e.target.value })} disabled={disabled} data-testid="intake-demo-email" /></Field>
        <Field label="Mobile *"><Input value={d.mobile} onChange={e => setD({ ...d, mobile: e.target.value })} disabled={disabled} data-testid="intake-demo-mobile" /></Field>
        <Field label="Street Address *" wide><Input value={d.street_address} onChange={e => setD({ ...d, street_address: e.target.value })} disabled={disabled} data-testid="intake-demo-street-address" /></Field>
        <Field label="Suburb *"><Input value={d.suburb} onChange={e => setD({ ...d, suburb: e.target.value })} disabled={disabled} data-testid="intake-demo-suburb" /></Field>
        <Field label="State *">
          <Select value={d.state || ''} onValueChange={(v) => setD({ ...d, state: v })} disabled={disabled}>
            <SelectTrigger data-testid="intake-demo-state"><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>
              {AU_STATE_OPTIONS.map(s => (
                <SelectItem key={s.code} value={s.code} data-testid={`intake-demo-state-${s.code.toLowerCase()}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Postcode *"><Input value={d.postcode} onChange={e => setD({ ...d, postcode: e.target.value })} disabled={disabled} data-testid="intake-demo-postcode" /></Field>
        <Field label="Primary language"><Input value={d.primary_language} onChange={e => setD({ ...d, primary_language: e.target.value })} disabled={disabled} /></Field>
        <Field label="Cultural background"><Input value={d.cultural_background} onChange={e => setD({ ...d, cultural_background: e.target.value })} disabled={disabled} /></Field>
        <Field label="Interpreter required">
          <label className="flex items-center gap-2"><Checkbox checked={!!d.interpreter_required} onCheckedChange={(v) => setD({ ...d, interpreter_required: !!v })} disabled={disabled} /> Yes</label>
        </Field>
      </div>
    </FormShell>
  );
}

// ─── NDIS & Funding ─────────────────────────────────────────────────────────
function NdisFundingForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ plan_name: '', plan_start_date: '', plan_end_date: '', total_budget: 0, plan_management_type: '', funding_source_type: 'NDIS', categories: [{ type: 'CORE', allocated: 0 }], ...data });
  const [dirty, setDirty] = useState(false);
  const updateCat = (i, k, v) => {
    const cats = [...(d.categories || [])];
    cats[i] = { ...cats[i], [k]: k === 'allocated' ? Number(v) : v };
    setD({ ...d, categories: cats });
  };
  const addCat = () => setD({ ...d, categories: [...(d.categories || []), { type: 'CORE', allocated: 0 }] });
  const removeCat = (i) => setD({ ...d, categories: (d.categories || []).filter((_, idx) => idx !== i) });
  return (
    <FormShell onSave={(c) => onSave(d, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Plan name"><Input value={d.plan_name} onChange={e => setD({ ...d, plan_name: e.target.value })} disabled={disabled} data-testid="intake-ndis-planname" /></Field>
        <Field label="Total budget ($)"><Input type="number" value={d.total_budget} onChange={e => setD({ ...d, total_budget: Number(e.target.value) })} disabled={disabled} data-testid="intake-ndis-totalbudget" /></Field>
        <Field label="Plan start date"><Input type="date" value={d.plan_start_date} onChange={e => setD({ ...d, plan_start_date: e.target.value })} disabled={disabled} /></Field>
        <Field label="Plan end date"><Input type="date" value={d.plan_end_date} onChange={e => setD({ ...d, plan_end_date: e.target.value })} disabled={disabled} /></Field>
        <Field label="Plan management">
          <Select value={d.plan_management_type || ''} onValueChange={(v) => setD({ ...d, plan_management_type: v })} disabled={disabled}>
            <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{PLAN_MGMT.map(p => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Funding source">
          <Select value={d.funding_source_type || 'NDIS'} onValueChange={(v) => setD({ ...d, funding_source_type: v })} disabled={disabled}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{FUNDING_SRC.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Budget categories</Label>
          <Button type="button" size="sm" variant="outline" onClick={addCat} disabled={disabled}><Plus className="h-3 w-3 mr-1" />Add category</Button>
        </div>
        {(d.categories || []).map((c, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <Select value={c.type} onValueChange={(v) => updateCat(i, 'type', v)} disabled={disabled}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{['CORE','CAPACITY','CAPITAL'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={c.allocated} onChange={e => updateCat(i, 'allocated', e.target.value)} className="w-40" disabled={disabled} />
            <Button type="button" size="sm" variant="ghost" onClick={() => removeCat(i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
          </div>
        ))}
      </div>
    </FormShell>
  );
}

// ─── Services ───────────────────────────────────────────────────────────────
function ServicesForm({ data, onSave, saving, disabled, catalog }) {
  const [rows, setRows] = useState(data && data.length ? data : []);
  const [dirty, setDirty] = useState(false);
  const update = (i, k, v) => { const r = [...rows]; r[i] = { ...r[i], [k]: v }; setRows(r); setDirty(true); };
  const add = () => { setRows([...rows, { service_catalog_id: '', day_of_week: 1, start_time: '09:00', end_time: '11:00' }]); setDirty(true); };
  const remove = (i) => { setRows(rows.filter((_, idx) => idx !== i)); setDirty(true); };
  // P-6.2 — block "mark complete" when no service rows have been added.
  const completeBlocked = rows.length === 0;
  return (
    <FormShell
      onSave={(c) => onSave(rows, c)}
      saving={saving}
      disabled={disabled}
      dirty={dirty}
      setDirty={setDirty}
      completeBlocked={completeBlocked}
      completeBlockedReason="Add at least one service before marking this section complete."
    >
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No services yet. Add at least one.</p>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2" data-testid={`intake-service-row-${i}`}>
            <div className="col-span-5">
              <Label className="text-xs">Service</Label>
              <LineItemSelect items={catalog} showRate={false} value={r.service_catalog_id} onChange={(v) => update(i, 'service_catalog_id', v)}
                disabled={disabled} placeholder="Select…" testId={`intake-service-${i}`} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Day</Label>
              <Select value={String(r.day_of_week)} onValueChange={(v) => update(i, 'day_of_week', Number(v))} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, idx) => <SelectItem key={idx} value={String(idx)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Start</Label><Input type="time" value={r.start_time} onChange={e => update(i, 'start_time', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-2"><Label className="text-xs">End</Label><Input type="time" value={r.end_time} onChange={e => update(i, 'end_time', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button></div>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={disabled} data-testid="intake-service-add"><Plus className="h-3 w-3 mr-1" /> Add service</Button>
      </div>
    </FormShell>
  );
}

// ─── Contacts ───────────────────────────────────────────────────────────────
function ContactsForm({ data, onSave, saving, disabled }) {
  const [rows, setRows] = useState(data && data.length ? data : []);
  const [dirty, setDirty] = useState(false);
  const update = (i, k, v) => { const r = [...rows]; r[i] = { ...r[i], [k]: v }; setRows(r); setDirty(true); };
  const add = () => { setRows([...rows, { first_name: '', last_name: '', relationship: 'PARENT', phone: '', email: '', is_emergency: true, is_primary: rows.length === 0 }]); setDirty(true); };
  const remove = (i) => { setRows(rows.filter((_, idx) => idx !== i)); setDirty(true); };
  // P-6.2 — block "mark complete" when no emergency contacts have been added.
  const completeBlocked = rows.length === 0;
  return (
    <FormShell
      onSave={(c) => onSave(rows, c)}
      saving={saving}
      disabled={disabled}
      dirty={dirty}
      setDirty={setDirty}
      completeBlocked={completeBlocked}
      completeBlockedReason="Add at least one emergency contact before marking this section complete."
    >
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">At least one emergency contact is required.</p>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2" data-testid={`intake-contact-row-${i}`}>
            <div className="col-span-3"><Label className="text-xs">First name *</Label><Input value={r.first_name} onChange={e => update(i, 'first_name', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-3"><Label className="text-xs">Last name *</Label><Input value={r.last_name} onChange={e => update(i, 'last_name', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-2">
              <Label className="text-xs">Relationship</Label>
              <Select value={r.relationship} onValueChange={(v) => update(i, 'relationship', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{RELATIONSHIPS.map(p => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Phone</Label><Input value={r.phone} onChange={e => update(i, 'phone', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-1 flex items-center gap-1 pt-5"><Checkbox checked={!!r.is_primary} onCheckedChange={(v) => update(i, 'is_primary', !!v)} disabled={disabled} /><span className="text-xs">1°</span></div>
            <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button></div>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={disabled} data-testid="intake-contact-add"><Plus className="h-3 w-3 mr-1" /> Add contact</Button>
      </div>
    </FormShell>
  );
}

// ─── Consents ───────────────────────────────────────────────────────────────
function ConsentsForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ service_agreement_signed: false, privacy_signed: false, photo_release_signed: false, signatory_name: '', signed_at: '', ...data });
  const [dirty, setDirty] = useState(false);
  return (
    <FormShell onSave={(c) => onSave(d, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      <div className="space-y-2">
        <label className="flex items-center gap-2"><Checkbox checked={!!d.service_agreement_signed} onCheckedChange={(v) => setD({ ...d, service_agreement_signed: !!v })} disabled={disabled} data-testid="intake-consent-sa" /> Service Agreement signed</label>
        <label className="flex items-center gap-2"><Checkbox checked={!!d.privacy_signed} onCheckedChange={(v) => setD({ ...d, privacy_signed: !!v })} disabled={disabled} data-testid="intake-consent-privacy" /> Privacy &amp; data consent signed</label>
        <label className="flex items-center gap-2"><Checkbox checked={!!d.photo_release_signed} onCheckedChange={(v) => setD({ ...d, photo_release_signed: !!v })} disabled={disabled} /> Photo / media release signed (optional)</label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Signatory name"><Input value={d.signatory_name} onChange={e => setD({ ...d, signatory_name: e.target.value })} disabled={disabled} /></Field>
        <Field label="Signed on"><Input type="date" value={d.signed_at} onChange={e => setD({ ...d, signed_at: e.target.value })} disabled={disabled} /></Field>
      </div>
    </FormShell>
  );
}

// ─── Goals ──────────────────────────────────────────────────────────────────
function GoalsForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ summary: '', preferences: '', ...data });
  const [dirty, setDirty] = useState(false);
  return (
    <FormShell onSave={(c) => onSave(d, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      <Field label="NDIS plan goals (summary)"><Textarea rows={4} value={d.summary} onChange={e => setD({ ...d, summary: e.target.value })} disabled={disabled} /></Field>
      <Field label="Preferences & communication"><Textarea rows={3} value={d.preferences} onChange={e => setD({ ...d, preferences: e.target.value })} disabled={disabled} /></Field>
    </FormShell>
  );
}

// ─── Accommodation ──────────────────────────────────────────────────────────
function AccommodationForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ accommodation_type: '', house_id: '', notes: '', ...data });
  const [dirty, setDirty] = useState(false);
  return (
    <FormShell onSave={(c) => onSave(d, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Accommodation type">
          <Select value={d.accommodation_type || ''} onValueChange={(v) => setD({ ...d, accommodation_type: v })} disabled={disabled}>
            <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {['FAMILY_HOME','PRIVATE_RENTAL','SIL','SDA','SUPPORTED_BOARDING','OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="House ID (if SIL/SDA)"><Input value={d.house_id} onChange={e => setD({ ...d, house_id: e.target.value })} disabled={disabled} /></Field>
      </div>
      <Field label="Notes"><Textarea rows={3} value={d.notes} onChange={e => setD({ ...d, notes: e.target.value })} disabled={disabled} /></Field>
    </FormShell>
  );
}

// ─── Clinical Baseline ──────────────────────────────────────────────────────
function ClinicalForm({ data, onSave, saving, disabled }) {
  const [d, setD] = useState({ allergies: [], conditions: [], ...data });
  const [dirty, setDirty] = useState(false);
  const updArr = (k, i, kk, v) => { const arr = [...(d[k] || [])]; arr[i] = { ...arr[i], [kk]: v }; setD({ ...d, [k]: arr }); setDirty(true); };
  const addArr = (k, init) => { setD({ ...d, [k]: [...(d[k] || []), init] }); setDirty(true); };
  const rmArr = (k, i) => { setD({ ...d, [k]: (d[k] || []).filter((_, idx) => idx !== i) }); setDirty(true); };
  return (
    <FormShell onSave={(c) => onSave(d, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      <p className="text-xs text-amber-700"><AlertTriangle className="h-3 w-3 inline mr-0.5" /> Optional but strongly recommended before first shift.</p>
      <RiskProfilePanel mode="intake_preview" intakeData={{ allergies: d.allergies || [], conditions: d.conditions || [], contacts: data?._contacts_for_preview || [] }} />
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Allergies</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => addArr('allergies', { allergen: '', allergen_type: 'DRUG', severity: 'MODERATE', reaction: '' })} disabled={disabled} data-testid="intake-clinical-add-allergy"><Plus className="h-3 w-3 mr-1" />Add allergy</Button>
        </div>
        {(d.allergies || []).map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 mb-1">
            <div className="col-span-3"><Label className="text-xs">Allergen</Label><Input value={a.allergen} onChange={e => updArr('allergies', i, 'allergen', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-2"><Label className="text-xs">Type</Label>
              <Select value={a.allergen_type} onValueChange={(v) => updArr('allergies', i, 'allergen_type', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ALLERGY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Severity</Label>
              <Select value={a.severity} onValueChange={(v) => updArr('allergies', i, 'severity', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ALLERGY_SEVS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-4"><Label className="text-xs">Reaction</Label><Input value={a.reaction} onChange={e => updArr('allergies', i, 'reaction', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => rmArr('allergies', i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button></div>
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Health conditions</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => addArr('conditions', { condition_name: '', category: 'CHRONIC', status: 'ACTIVE', severity: 'MODERATE' })} disabled={disabled}><Plus className="h-3 w-3 mr-1" />Add condition</Button>
        </div>
        {(d.conditions || []).map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 mb-1">
            <div className="col-span-4"><Label className="text-xs">Condition</Label><Input value={c.condition_name} onChange={e => updArr('conditions', i, 'condition_name', e.target.value)} disabled={disabled} /></div>
            <div className="col-span-2"><Label className="text-xs">Category</Label>
              <Select value={c.category} onValueChange={(v) => updArr('conditions', i, 'category', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COND_CATEGORIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Status</Label>
              <Select value={c.status} onValueChange={(v) => updArr('conditions', i, 'status', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COND_STATUSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3"><Label className="text-xs">Severity</Label>
              <Select value={c.severity} onValueChange={(v) => updArr('conditions', i, 'severity', v)} disabled={disabled}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COND_SEVS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => rmArr('conditions', i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button></div>
          </div>
        ))}
      </div>
    </FormShell>
  );
}

// ─── Documents (placeholder note list) ──────────────────────────────────────
// ─── Documents — Phase 2B real uploader (additive; falls back to reference-only
// placeholder when participant_intake_documents_enabled flag is OFF) ─────────
function DocumentsForm({ data, onSave, saving, disabled }) {
  const { id: intakeId } = useParams();
  const [rows, setRows] = useState(data && data.length ? data : []);
  const [dirty, setDirty] = useState(false);

  // Phase 2B feature flag — same localStorage cache used elsewhere
  let intakeDocsEnabled = false;
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    intakeDocsEnabled = !!flags.participant_intake_documents_enabled && !!flags.participant_intake_wizard_enabled;
  } catch { /* default false */ }

  // Real document uploader path
  const [uploads, setUploads] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingCategory, setPendingCategory] = useState('NDIS_PLAN');
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingExpiry, setPendingExpiry] = useState('');

  const loadUploads = async () => {
    if (!intakeDocsEnabled || !intakeId) return;
    setLoadingDocs(true);
    try {
      const r = await api.get(`/intake/participants/${intakeId}/documents`);
      setUploads(r.data?.data || []);
    } catch (e) {
      // 404 FEATURE_DISABLED handled silently — placeholder still works
    } finally {
      setLoadingDocs(false);
    }
  };
  useEffect(() => { loadUploads(); /* eslint-disable-next-line */ }, [intakeId, intakeDocsEnabled]);

  const handleFile = async (file) => {
    if (!file || !intakeDocsEnabled || disabled) return;
    if (!pendingTitle.trim()) { toast.error('Please enter a title for the document.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', pendingCategory);
      fd.append('title', pendingTitle.trim());
      if (pendingExpiry) fd.append('expiryDate', pendingExpiry);
      await api.post(`/intake/participants/${intakeId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded.');
      setPendingTitle('');
      setPendingExpiry('');
      await loadUploads();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = async (docId) => {
    try {
      await api.delete(`/intake/participants/${intakeId}/documents/${docId}`);
      toast.success('Document removed.');
      await loadUploads();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Remove failed.');
    }
  };

  // Reference-row helpers (placeholder fallback when flag OFF)
  const add = () => { setRows([...rows, { name: '', type: 'NDIS_PLAN', notes: '' }]); setDirty(true); };
  const remove = (i) => { setRows(rows.filter((_, idx) => idx !== i)); setDirty(true); };
  const update = (i, k, v) => { const r = [...rows]; r[i] = { ...r[i], [k]: v }; setRows(r); setDirty(true); };

  return (
    <FormShell onSave={(c) => onSave(rows, c)} saving={saving} disabled={disabled} dirty={dirty} setDirty={setDirty}>
      {intakeDocsEnabled ? (
        <>
          <p className="text-xs text-muted-foreground">Upload onboarding documents now. They will be linked to the participant automatically on activation.</p>
          {loadingDocs ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="space-y-1" data-testid="intake-uploaded-docs-list">
              {uploads.length === 0 && <p className="text-xs text-muted-foreground italic">No documents uploaded yet.</p>}
              {uploads.map(u => (
                <div key={u.id} className="flex items-center justify-between border rounded-md p-2 text-sm" data-testid={`intake-doc-${u.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{u.title}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{u.category}</span>
                    {u.fileName && <span className="text-xs text-muted-foreground">{u.fileName}</span>}
                  </div>
                  {!disabled && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeUpload(u.id)} data-testid={`intake-doc-remove-${u.id}`}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!disabled && (
            <div className="border rounded-md p-3 space-y-2 bg-muted/20" data-testid="intake-doc-uploader">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4"><Label className="text-xs">Title *</Label><Input value={pendingTitle} onChange={e => setPendingTitle(e.target.value)} placeholder="e.g. NDIS Plan Jan 2026" data-testid="intake-doc-title" /></div>
                <div className="col-span-3"><Label className="text-xs">Category</Label>
                  <Select value={pendingCategory} onValueChange={setPendingCategory}>
                    <SelectTrigger data-testid="intake-doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['NDIS_PLAN','SERVICE_AGREEMENT','CONSENT','BEHAVIOUR_SUPPORT_PLAN','MEDICAL','ID','GUARDIAN_DOCUMENT','RISK_ASSESSMENT_DOC','OTHER'].map(c => (
                        <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3"><Label className="text-xs">Expiry (optional)</Label><Input type="date" value={pendingExpiry} onChange={e => setPendingExpiry(e.target.value)} /></div>
                <div className="col-span-2"><Label className="text-xs">File</Label>
                  <Input type="file" disabled={uploading || !pendingTitle.trim()} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} data-testid="intake-doc-file" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{uploading ? 'Uploading…' : 'Choose a file to upload.'}</p>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground border-t pt-2">Reference-only notes (legacy):</p>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">Document references (file upload requires the <code>participant_intake_documents_enabled</code> feature flag).</p>
      )}

      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 mb-1">
          <div className="col-span-4"><Label className="text-xs">Name</Label><Input value={r.name} onChange={e => update(i, 'name', e.target.value)} disabled={disabled} /></div>
          <div className="col-span-3"><Label className="text-xs">Type</Label>
            <Select value={r.type} onValueChange={(v) => update(i, 'type', v)} disabled={disabled}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['NDIS_PLAN','ID','SUPPORT_LETTER','MEDICAL','OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-4"><Label className="text-xs">Notes</Label><Input value={r.notes} onChange={e => update(i, 'notes', e.target.value)} disabled={disabled} /></div>
          <div className="col-span-1"><Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} disabled={disabled}><Trash2 className="h-3 w-3 text-rose-600" /></Button></div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} disabled={disabled}><Plus className="h-3 w-3 mr-1" /> Add document reference</Button>
    </FormShell>
  );
}

// ─── Small reusable Field wrapper ───────────────────────────────────────────
function Field({ label, children, wide }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
